import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — four levels up from src/pages/insurance/types/
import type {
  TInsuranceGlobalTypeListItem,
  TInsuranceProviderListItem,
  ITypeWizardStep,
} from '@/@types/insuranceBuilder';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useCreateTypeWizard } from '@/hooks/useCreateTypeWizard';
import { isValidProviderId, isValidOrgTypeId, isValidStartingSource } from '@/lib/type-guards';
import { buildSeedSections } from '@/lib/insurance/buildSeedSections';
import { TypeWizardStepper } from '@/components/insurance/types/Wizard/Stepper';
import { SourceCardGrid } from '@/components/insurance/types/Wizard/SourceCardGrid';
import { LibraryStatsRail } from '@/components/insurance/types/Wizard/LibraryStatsRail';
import { TypeNameCombobox } from '@/components/insurance/types/Wizard/TypeNameCombobox';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function CreateTypeStep1Page() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const { state, setProviderId, setName, setSource, setCopyFromId, setSubmitting } =
    useCreateTypeWizard();

  const providers = useQuery(
    api['insurance/providers'].list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const globalTypes = useQuery(api['insurance/types'].listGlobalTypes, {});
  const orgTypes = useQuery(
    api['insurance/types'].listByProvider,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const copySource = useQuery(
    api['insurance/types'].getByIdWithSections,
    state.source === 'copy' && state.copyFromId ? { typeId: state.copyFromId } : 'skip'
  );
  const upsert = useMutation(api['insurance/types'].upsertWithSections);

  if (orgLoading || !currentOrg) return null;

  const matchedPredefined =
    globalTypes?.find(
      (g: TInsuranceGlobalTypeListItem) => g.name.toLowerCase() === state.name.trim().toLowerCase()
    ) ?? null;
  const predefinedEnabled = matchedPredefined !== null;
  const canContinue = !!state.providerId && state.name.trim().length > 0 && !state.submitting;

  const wizardSteps: ITypeWizardStep[] = [
    { key: 'source', label: 'Source' },
    ...(state.source === 'ai' ? [{ key: 'extract' as const, label: 'Extract' }] : []),
    { key: 'builder', label: 'Builder' },
    { key: 'review', label: 'Review' },
  ];

  const handleContinue = async () => {
    if (!canContinue || !state.providerId) return;
    if (state.source === 'ai') {
      navigate('/insurance/types/new/extract');
      return;
    }
    setSubmitting(true);
    try {
      const sections = buildSeedSections({
        source: state.source,
        matchedPredefined,
        copySource: copySource ?? null,
      });
      const newId = await upsert({
        orgId: currentOrg._id,
        providerId: state.providerId,
        name: state.name.trim(),
        sections,
      });
      navigate(`/insurance/types/${newId}`);
    } catch (error: unknown) {
      const msg =
        error instanceof Error && 'data' in error
          ? String((error as Error & { data: unknown }).data) // AS_JUSTIFIED: ConvexError always has .data field containing the message string
          : undefined;
      if (msg && msg.includes('already exists')) {
        toast.warning(msg);
      } else {
        toast.error(msg ?? 'Failed to create insurance type');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/insurance/types')}
            className="gap-1 px-2"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            <span className="sr-only">Back</span>
          </Button>
          <h1 className="text-xl font-semibold text-foreground">Create insurance type</h1>
        </div>

        <TypeWizardStepper steps={wizardSteps} currentIndex={0} />

        {state.source !== 'ai' && (
          <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Skip the form — let AI do it</p>
              <p className="text-xs text-muted-foreground">
                Upload a policy PDF and we extract all fields automatically.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setSource('ai')}>
              Try AI Extract
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px]">
          <div className="space-y-4">
            <Card className="p-5 space-y-3">
              <Label htmlFor="provider-select">Provider</Label>
              <Select
                value={state.providerId ?? ''}
                onValueChange={v => setProviderId(isValidProviderId(v) ? v : undefined)}
              >
                <SelectTrigger id="provider-select">
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {(providers ?? []).map((p: TInsuranceProviderListItem) => (
                    <SelectItem key={p._id} value={p._id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Card>

            <Card className="p-5 space-y-3">
              <Label htmlFor="type-name">Type name</Label>
              <TypeNameCombobox
                value={state.name}
                onChange={setName}
                options={(globalTypes ?? []).map((g: TInsuranceGlobalTypeListItem) => ({
                  _id: g._id,
                  name: g.name,
                }))}
              />
            </Card>

            <Card className="p-5 space-y-3">
              <p className="text-sm font-medium text-foreground">Starting fields</p>
              <SourceCardGrid
                source={state.source}
                predefinedEnabled={predefinedEnabled}
                orgTypes={orgTypes}
                copyFromId={state.copyFromId}
                onSelect={v => {
                  if (isValidStartingSource(v)) setSource(v);
                }}
                onCopyFromId={v => setCopyFromId(isValidOrgTypeId(v) ? v : undefined)}
              />
            </Card>
          </div>

          <LibraryStatsRail orgId={currentOrg._id} />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleContinue} disabled={!canContinue}>
            {state.submitting ? 'Creating…' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
